import appCache from './AppCache';

interface PersistedSectorSelection {
  sectorId: number;
  isOfficial: boolean;
}

class SessionService {
  private static readonly TOKEN_KEY = '@app/token';
  private static readonly USER_KEY = '@app/user';
  private static readonly SECTOR_KEY = '@app/sector';
  private static readonly SECTOR_OFFICIAL_KEY = '@app/sector_official';
  private static readonly AVAILABLE_SECTORS_KEY = '@app/available_sectors';
  private static readonly SELECTED_SECTOR_PREFIX = '@app/selected_sector:';

  // Salva um valor na sessão
  static setSession(key: string, value: any): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  // Decodifica um token JWT
  static decodeToken(token: string): any | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }

  static isTokenExpired(token: string): boolean {
    try {
      const payload = this.decodeToken(token);
      if (!payload || !payload.exp) return true;
      return Date.now() >= payload.exp * 1000;
    } catch {
      return true;
    }
  }

  // Recupera um item da sessão para o setor
  static getSessionForSector(): any | null {
    const sessionData = localStorage.getItem("selectedSector");
    if (!sessionData) {
      return null;
    }

    return JSON.parse(sessionData); // Retorna qualquer tipo
  }

  // Limpa a sessão do setor
  static clearSectorSession(): void {
    this.removeSession("selectedSector");
  }

  // Recupera um item específico da sessão
  static getSession(key: string): any | null {
    const value = localStorage.getItem(key);
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  // Remove um item específico da sessão
  static removeSession(key: string): void {
    localStorage.removeItem(key);
  }

  // Limpa toda a sessão armazenada
  static clearAllSessions(): void {
    localStorage.clear();
  }

  // Verifica se uma sessão específica está expirada
  static isSessionExpired(key: string): boolean {
    const sessionData = localStorage.getItem(key);
    if (!sessionData) {
      return true;
    }

    const parsedData = JSON.parse(sessionData);
    const token = parsedData;

    if (!token) {
      return true;
    }

    const decodedToken = this.decodeToken(token);
    if (!decodedToken || !decodedToken.exp) {
      return true;
    }

    const currentTimeInSeconds = Math.floor(new Date().getTime() / 1000);
    return decodedToken.exp < currentTimeInSeconds;
  }

  static setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  static getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  static removeToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
  }

  static setUser(user: any): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  static getUser(): any | null {
    const user = localStorage.getItem(this.USER_KEY);
    return user ? JSON.parse(user) : null;
  }

  static removeUser(): void {
    localStorage.removeItem(this.USER_KEY);
  }

  static setAvailableSectors(sectors: number[]) {
    localStorage.setItem(this.AVAILABLE_SECTORS_KEY, JSON.stringify(sectors));
  }

  static getAvailableSectors(): number[] {
    const sectors = localStorage.getItem(this.AVAILABLE_SECTORS_KEY);
    return sectors ? JSON.parse(sectors) : [];
  }

  private static _currentSectorId: number | null = null;
  private static _currentSectorIsOfficial: boolean | null = null;

  private static getPersistedSectorKey(): string | null {
    const userId = this.getCurrentUserId();
    if (!userId) return null;
    return `${this.SELECTED_SECTOR_PREFIX}${userId}`;
  }

  static getPersistedSector(): PersistedSectorSelection | null {
    const key = this.getPersistedSectorKey();
    if (!key) return null;

    const raw = localStorage.getItem(key);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as PersistedSectorSelection;
      const sectorId = Number(parsed?.sectorId);
      if (!Number.isInteger(sectorId) || sectorId <= 0 || sectorId > 2147483647) return null;
      return {
        sectorId,
        isOfficial: Boolean(parsed.isOfficial),
      };
    } catch {
      return null;
    }
  }

  static persistSelectedSector(sectorId: number, isOfficial: boolean): void {
    const key = this.getPersistedSectorKey();
    if (!key) return;

    localStorage.setItem(
      key,
      JSON.stringify({ sectorId, isOfficial: Boolean(isOfficial) } satisfies PersistedSectorSelection)
    );
  }

  static clearPersistedSector(): void {
    const key = this.getPersistedSectorKey();
    if (key) localStorage.removeItem(key);
  }

  static invalidateSectorCache(sectorId?: number | null): void {
    if (sectorId) {
      appCache.invalidate(`dashboard:${sectorId}`);
      appCache.invalidate(`tags:${sectorId}`);
      appCache.invalidate(`contacts:${sectorId}:*`);
    }
    appCache.invalidate('bootstrap');
  }

  static revokeSectorSelection(sectorId?: number | null): void {
    const id = sectorId ?? this._currentSectorId;
    this.clearPersistedSector();
    this.invalidateSectorCache(id);
    this.removeSectorId();
  }

  static setSectorId(sectorId: number, isOfficial: boolean) {
    // Validar e converter os valores
    if (!sectorId || typeof sectorId !== 'number') {
      console.error('Invalid sectorId:', sectorId);
      return;
    }

    const officialValue = Boolean(isOfficial);

    // Verificar se o setor atual é diferente do novo
    if (this._currentSectorId === sectorId && this._currentSectorIsOfficial === officialValue) {
      return; // Se for o mesmo setor e mesmo status, não faz nada
    }

    this._currentSectorId = sectorId;
    this._currentSectorIsOfficial = officialValue;
    this.persistSelectedSector(sectorId, officialValue);

    // Atualizar setores disponíveis se necessário
    const availableSectors = this.getAvailableSectors();
    if (!availableSectors.includes(sectorId)) {
      this.setAvailableSectors([...availableSectors, sectorId]);
    }

    // Disparar evento de mudança apenas se houve mudança real
    const event = new CustomEvent('sectorChanged', {
      detail: { sectorId, isOfficial: officialValue }
    });
    window.dispatchEvent(event);
  }

  /** Only returns the in-memory sector validated by SectorContext — never reads localStorage directly. */
  static getSectorId(): number | null {
    return this._currentSectorId;
  }

  static getSectorIsOfficial(): boolean | null {
    return this._currentSectorIsOfficial;
  }

  static removeSectorId() {
    this._currentSectorId = null;
    this._currentSectorIsOfficial = null;
    this.clearPersistedSector();

    const event = new CustomEvent('sectorChanged', {
      detail: { sectorId: null, isOfficial: null }
    });
    window.dispatchEvent(event);
  }

  static validateAndCleanSectorSession(availableSectors: number[]) {
    // Como agora é em memória, não precisamos "limpar" do storage,
    // mas ainda podemos querer validar contra a lista atual para garantir consistência
    if (!Array.isArray(availableSectors)) {
      console.error('Invalid availableSectors:', availableSectors);
      return;
    }

    this.setAvailableSectors(availableSectors);
    const currentSectorId = this.getSectorId();

    if (currentSectorId && !availableSectors.includes(currentSectorId)) {
      console.log('Current sector not in available sectors, removing...', {
        currentSectorId,
        availableSectors
      });
      this.revokeSectorSelection(currentSectorId);
    }
  }

  static isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;

    return !this.isTokenExpired(token);
  }

  static clearSession() {
    this.clearPersistedSector();
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.AVAILABLE_SECTORS_KEY);

    this._currentSectorId = null;
    this._currentSectorIsOfficial = null;
  }

  static setIsAnonymous(isAnonymous: boolean) {
    localStorage.setItem('@app/is_anonymous', JSON.stringify(isAnonymous));
  }

  static getIsAnonymous(): boolean {
    const value = localStorage.getItem('@app/is_anonymous');
    return value ? JSON.parse(value) : false;
  }

  // Função para verificar se o usuário atual é admin
  static isCurrentUserAdmin(): boolean {
    try {
      const token = this.getToken();
      if (!token) return false;

      const decodedToken = this.decodeToken(token);
      if (!decodedToken) return false;

      // Verificar múltiplos formatos possíveis no token
      return decodedToken.isAdmin === "true" ||
        decodedToken.isAdmin === true ||
        decodedToken.role === "Admin";
    } catch (error) {
      console.error('Erro ao verificar se usuário é admin:', error);
      return false;
    }
  }

  // Função para obter o ID do usuário atual
  static getCurrentUserId(): number | null {
    try {
      const token = this.getToken();
      if (!token) return null;

      const decodedToken = this.decodeToken(token);
      if (!decodedToken || !decodedToken.userId) return null;

      return Number(decodedToken.userId);
    } catch (error) {
      console.error('Erro ao obter ID do usuário:', error);
      return null;
    }
  }
}

export default SessionService;
